import { faCheck, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { Skeleton } from 'src/components/Skeleton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

import { ActionsList } from './ActionsList';
import { BlockingReasonAction } from './_types';
import {
  blockingReasonIconSx,
  blockingReasonRowBackground,
  blockingReasonRowBorderColor,
  blockingReasonRowSx,
  blockingReasonWideMin,
} from './blockingReasonStyles';

/**
 * Viewport breakpoints are wrong inside the booking embed's half-width column
 * (iframe ~650px → sm matches, but the column is ~325px). Container queries
 * stack actions under the copy until the row itself is wide enough.
 */
const blockingReasonRowLayoutSx = {
  ...blockingReasonRowSx,
  flexDirection: 'column',
  alignItems: 'stretch',
  [`@container (min-width: ${blockingReasonWideMin}px)`]: {
    flexDirection: 'row',
    alignItems: 'center',
  },
} as const;

const blockingReasonActionsLayoutSx = {
  flexShrink: 0,
  width: '100%',
  alignSelf: 'stretch',
  '& .MuiButton-root, & a, & button': {
    width: '100%',
  },
  [`@container (min-width: ${blockingReasonWideMin}px)`]: {
    width: 'auto',
    alignSelf: 'center',
    '& .MuiButton-root, & a, & button': {
      width: 'auto',
    },
  },
} as const;

export function BlockingReasonRow({
  title,
  message,
  actions,
  color = 'error',
  iconVariant = 'alert',
}: {
  title?: string;
  message?: string;
  actions?: BlockingReasonAction[];
  color?: 'error' | 'success';
  iconVariant?: 'alert' | 'approved';
}) {
  const showApprovedIcon = iconVariant === 'approved' || color === 'success';
  const primaryText = title || message;
  const showSecondaryMessage = !!title && !!message;

  return (
    <Box sx={{ containerType: 'inline-size', width: '100%' }}>
      <Stack
        spacing={1}
        sx={{
          ...blockingReasonRowLayoutSx,
          bgcolor: blockingReasonRowBackground(color),
          borderColor: blockingReasonRowBorderColor(color),
        }}
      >
        <Stack
          spacing={0.5}
          sx={{
            flex: 1,
            minWidth: 0,
            py: 0.5,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Icon
              icon={showApprovedIcon ? faCheck : faExclamationCircle}
              size="small"
              sx={{
                ...blockingReasonIconSx,
                color: `${color}.solid`,
                flexShrink: 0,
                mt: 0.25,
              }}
            />
            {!!primaryText && (
              <Typography
                variant={title ? 'h6' : 'body2'}
                sx={{ flex: 1, minWidth: 0 }}
              >
                {primaryText}
              </Typography>
            )}
          </Stack>
          {showSecondaryMessage && (
            <Typography variant="body2" sx={{ pl: 3.5 }}>
              {message}
            </Typography>
          )}
        </Stack>

        {!!actions && (
          <Stack sx={blockingReasonActionsLayoutSx}>
            <ActionsList actions={actions} />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export function BlockingReasonFallback() {
  return (
    <Box sx={{ containerType: 'inline-size', width: '100%' }}>
      <Stack
        spacing={1}
        sx={{
          ...blockingReasonRowLayoutSx,
          bgcolor: blockingReasonRowBackground('error'),
          borderColor: blockingReasonRowBorderColor('error'),
        }}
      >
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon
              icon={faExclamationCircle}
              size="small"
              sx={{ ...blockingReasonIconSx, flexShrink: 0 }}
            />
            <Skeleton variant="text" width="50%" height={24} sx={{ flex: 1 }} />
          </Stack>
          <Skeleton variant="text" width="70%" height={24} sx={{ pl: 3.5 }} />
        </Stack>
      </Stack>
    </Box>
  );
}
